import { Box } from "@mui/material"
import { PoolDataType, UserShareType } from "../hooks/usePoolData"
import React, { ReactElement, useState } from "react"
import { BigNumber } from "@ethersproject/bignumber"
import ReviewWithdraw from "./ReviewWithdraw"
import { WithdrawFormState } from "../hooks/useWithdrawFormState"
import { Zero } from "@ethersproject/constants"
import { formatBNToPercentString } from "../utils"
import { logEvent } from "../utils/googleAnalytics"
import { useTranslation } from "react-i18next"
import Text from "./Text"
import CustomCard from "./CustomCard"
import AmountInput from "./AmountInput"
import { BIG_ZERO } from "src/constants/bigNumber"
import Button from "./Button"
import CroWarning from "./CroWarning"
import { ChainId, POOLS_MAP, PoolTypes } from "src/constants"
import Modal from "./Modal"
import { TransactionSummaryData } from "./TransactionSummary"
import parseStringToBigNumber from "src/utils/parseStringToBigNumber"
import ToggleOptions from "./ToggleOptions"
import SlippageRow from "./SlippageRow"
import { useActiveWeb3React } from "src/hooks"
import Web3Status from "./Web3Status"

export interface ReviewWithdrawData {
  withdraw: {
    name: string
    value: string
    icon: string
    symbol: string
    decimals: number
    addresses: { [chainId in ChainId]: string }
  }[]
  rates: {
    name: string
    value: string
    rate: string
  }[]
  slippage: string
  priceImpact: BigNumber
  txnGasCost: {
    amount: BigNumber
    valueUSD: BigNumber | null // amount * ethPriceUSD
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
interface Props {
  title: string
  tokensData: Array<{
    symbol: string
    name: string
    icon: string
    inputValue: string
    decimals: number
  }>
  reviewData: ReviewWithdrawData
  selected?: { [key: string]: any }
  poolData: PoolDataType | null
  myShareData: UserShareType | undefined
  formStateData: WithdrawFormState
  onFormChange: (action: any) => void
  onConfirmTransaction: (summary: TransactionSummaryData) => Promise<void>
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const WithdrawPage = (props: Props): ReactElement => {
  const { t } = useTranslation()
  const { account } = useActiveWeb3React()
  const {
    tokensData,
    poolData,
    myShareData,
    onFormChange,
    formStateData,
    reviewData,
    onConfirmTransaction,
  } = props

  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const pool = POOLS_MAP[poolData?.name || ""]

  const onSubmit = (): void => {
    setShowConfirmModal(true)
  }

  const handleWithdrawChange = (value: string) => {
    onFormChange({
      fieldName: "withdrawType",
      value,
    })
  }
  const noShare = !myShareData || myShareData.lpTokenBalance.eq(Zero)
  const summary: TransactionSummaryData = [
    ...reviewData.withdraw.map(
      ({ name, value, icon, symbol, addresses, decimals }) => ({
        label: t("Receive (Min)"),
        amount: value,
        token: { icon, symbol, name, addresses, decimals },
      }),
    ),
  ]

  const insufficientAmount = parseStringToBigNumber(
    formStateData.amount,
    18,
  ).value.gt(myShareData?.lpTokenBalance || BIG_ZERO)

  return (
    <Box>
      <Box>
        <CustomCard variant="black50">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" width="35%" mr={1}>
              {pool && <img src={pool.lpToken.icon} height={16} />}
              <Text ml={1}>{pool.lpToken.symbol}</Text>
            </Box>
            <Box width="65%" pb={3}>
              <AmountInput
                balance={myShareData?.lpTokenBalance || BIG_ZERO}
                decimals={18}
                tokenSymbol={poolData?.lpToken || ""}
                showMax
                hidePercentageButtons
                onChange={(value) =>
                  onFormChange({
                    fieldName: "amount",
                    value,
                  })
                }
                value={formStateData.amount}
              />
            </Box>
          </Box>
        </CustomCard>
      </Box>
      <Box mt={3}>
        <Text mb={1}>{t("Output")}</Text>
        <ToggleOptions
          options={[
            { label: "ALL", value: "ALL", icon: pool?.lpToken.icon },
            ...tokensData.map((t) => ({
              label: t.symbol,
              value: t.symbol,
              icon: t.icon,
            })),
          ]}
          value={formStateData.withdrawType}
          onChange={handleWithdrawChange}
        />
      </Box>
      {pool.type === PoolTypes.CRO && (
        <CroWarning content="WCRO in this pool will be unwrapped as CRO." />
      )}
      <Box mt={2}>
        {tokensData.map((token) => (
          <Box
            display="flex"
            justifyContent="space-between"
            key={token.name}
            mb={1}
          >
            <Text>{t("Receive (Min)")}</Text>
            <Box display="flex" alignItems="center">
              <Text>
                {reviewData.withdraw.find((x) => x.symbol === token.symbol)
                  ?.value || "0"}
              </Text>
              <Text ml={0.5} mr={1}>
                {token.symbol}
              </Text>
              <img height={16} width={16} src={token.icon} />
            </Box>
          </Box>
        ))}
        <Box mb={1}>
          <SlippageRow />
        </Box>
      </Box>
      <Box my={3}>
        <Text
          gradient={
            reviewData.priceImpact.gte(0) ? "gradient1" : "gradient-orange"
          }
          borderRadius="10px"
          py={0.5}
          px={2}
          size="micro"
        >
          {reviewData.priceImpact.gte(0) ? t("Bonus: ") : t("Price Impact: ")}
          {reviewData.priceImpact.gt(0) ? "+" : ""}
          {formatBNToPercentString(reviewData.priceImpact, 18, 4)}
        </Text>
      </Box>
      {account ? (
        <Button
          fullWidth
          disabled={
            noShare ||
            !!formStateData.error ||
            formStateData.lpTokenAmountToSpend.isZero() ||
            insufficientAmount
          }
          onClick={onSubmit}
        >
          {insufficientAmount
            ? t("Insufficient Balance")
            : t("Remove Liquidity")}
        </Button>
      ) : (
        <Web3Status fullWidth />
      )}
      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Transaction"
      >
        <ReviewWithdraw
          transactionSummary={summary}
          onConfirm={async (): Promise<void> => {
            setShowConfirmModal(false)
            logEvent("withdraw", (poolData && { pool: poolData?.name }) || {})
            await onConfirmTransaction(summary)
          }}
        />
      </Modal>
    </Box>
  )
}

export default WithdrawPage
